<template>
  <div class="w-full h-full flex flex-col lg:flex-row mx-0">
    <!--    left container -->
    <div class="w-full h-full lg:w-1/2 flex">
      <!--      inner container -->
      <div class="container m-auto w-4/5 xl:w-[485px] h-5/6 sm:h-4/5 lg:h-2/4 flex flex-col justify-between">
        <!--        text container -->
        <div class="h-[228px] sm:h-auto">
          <transition
            appear
            name="fade"
            mode="out-in"
          >
            <p
              class="text-3xl sm:text-4xl font-bold mb-6 sm:mb-7"
              v-if="currentStep === 1"
              key="1"
            >
              Welcome to Financial&nbsp;Cents 👋
            </p>
            <p
              class="text-3xl sm:text-4xl font-bold mb-6 sm:mb-7"
              v-else-if="currentStep === 2"
              key="2"
            >
              Always know the status of work
            </p>
            <p
              class="text-3xl sm:text-4xl font-bold mb-6 sm:mb-7"
              v-else-if="currentStep === 3"
              key="3"
            >
              Get client responses faster
            </p>
            <p
              class="text-3xl sm:text-4xl font-bold mb-6 sm:mb-7"
              v-else-if="currentStep === 4"
              key="4"
            >
              Centralize everything in one place
            </p>
            <p
              class="text-3xl sm:text-4xl font-bold mb-6 sm:mb-7"
              v-else-if="currentStep === 5"
              key="5"
            >
              So your firm can hit client deadlines
            </p>
          </transition>
          <transition
            appear
            name="slide-fade"
            mode="out-in"
          >
            <p
              class="text-md sm:text-lg font-light"
              v-if="currentStep === 1"
              key="1"
            >
              In the next 20 seconds, you will see how Financial Cents makes it easy for your firm to get work done.
            </p>
            <p
              class="text-md sm:text-lg font-light"
              v-else-if="currentStep === 2"
              key="2"
            >
              Easily track the status of work and who is working on what so you never miss a client deadline again.
            </p>
            <p
              class="text-md sm:text-lg font-light"
              v-else-if="currentStep === 3"
              key="3"
            >
              Never chase down clients for information again with automated requests and an easy-to-use client portal.
            </p>
            <p
              class="text-md sm:text-lg font-light"
              v-else-if="currentStep === 4"
              key="4"
            >
              Organize work, documents, communication, and client information in one workflow space to enable your team
              to get work done.
            </p>
            <p
              class="text-md sm:text-lg font-light"
              v-else-if="currentStep === 5"
              key="5"
            >
              Everything your firm needs to ensure you never miss a client deadline again.
            </p>
          </transition>
        </div>

        <!--        video shown here on smaller screens -->
        <div
          v-if="!isLargeScreen"
          class="block lg:hidden w-full sm:w-10/12 lg:w-1/2 max-w-full h-auto lg:w-1/2 sm:max-w-1/2 border rounded-xl my-3 mx-auto lg:m-5 overflow-hidden bg-[#E7FAED]"
        >
          <div class="video-container w-full flex">
            <video
              v-for="(videoName, index) in videos"
              :key="videoName"
              :ref="'video' + (index + 1)"
              v-show="currentStep === index + 1"
              preload="auto"
              muted
              playsinline
              loop
              class="static lg:absolute"
              :class="{
                'h-full object-top-center object-cover lg:object-center mx-auto w-full w-[324px] lg:w-full':
                  currentStep === 1,
                'h-full object-left-center sm:object-top md:object-left-center object-cover lg:object-left-center mx-auto md:w-10/12 lg:w-full':
                  currentStep > 1,
              }"
            >
              <source
                :src="'/videos/onboarding/' + videoName + '.webm'"
                type="video/webm"
              />
              <source
                :src="'/videos/onboarding/' + videoName + '.mp4'"
                type="video/mp4"
              />
            </video>
          </div>
        </div>

        <!--        button/steps container  -->
        <div>
          <div>
            <!--          button/steps -->
            <div class="flex justify-between mb-3">
              <!--            button -->
              <div>
                <v-button
                  @click="nextStep"
                  class="bg-green-600 hover:bg-green-700 rounded px-12 py-2 text-white"
                >
                  <template #title>
                    <span class="flex items-center">
                      {{ currentStep === 5 ? "Get started" : "Next" }}
                      <ion-icon name="chevron-forward-outline"></ion-icon>
                    </span>
                  </template>
                </v-button>
              </div>

              <!--            steps-->
              <div class="flex w-28">
                <div
                  v-for="(step, index) in 5"
                  :key="index"
                  class="rounded-full h-[8px] w-[8px] m-auto"
                  :class="{
                    'bg-black': index + 1 <= currentStep,
                    'bg-gray-300': index + 1 > currentStep,
                  }"
                ></div>
              </div>
            </div>
          </div>

          <!--          skip -->
          <div class="h-[25px]">
            <a
              href="#"
              class="underline text-gray-400"
              @click.prevent="handleSkip"
              v-show="currentStep === 1"
              >Skip 20 second overview</a
            >
          </div>
        </div>
      </div>
    </div>
    <!--    right container -->
    <div
      v-if="isLargeScreen"
      class="hidden lg:block w-11/12 lg:w-1/2 h-full max-w-full lg:h-auto lg:w-1/2 sm:max-w-1/2 border rounded-xl my-3 mx-auto lg:m-5 overflow-hidden bg-[#E7FAED]"
    >
      <div class="video-container w-full flex">
        <video
          v-for="(videoName, index) in videos"
          :key="videoName"
          :ref="'video' + (index + 1)"
          v-show="currentStep === index + 1"
          preload="auto"
          muted
          playsinline
          loop
          class="static lg:absolute"
          :class="{
            'h-full object-top-center object-cover lg:object-center mx-auto w-full sm:w-3/5 lg:w-full':
              currentStep === 1,
            'h-full object-left-center sm:object-top md:object-center object-cover lg:object-left-center mx-auto md:w-3/5 lg:w-full':
              currentStep > 1,
          }"
        >
          <source
            :src="'/videos/onboarding/' + videoName + '.webm'"
            type="video/webm"
          />
          <source
            :src="'/videos/onboarding/' + videoName + '.mp4'"
            type="video/mp4"
          />
        </video>
      </div>
    </div>
  </div>
</template>

<script>
import VButton from "../../../../ClientPortal/components/VButton.vue"

export default {
  name: "OnboardingSlidesStepper",

  components: {
    VButton,
  },

  emits: ["onboardingComplete"],

  data() {
    return {
      currentStep: 1,
      isLargeScreen: false,
      videos: [
        "welcome-to-fc",
        "status-of-work",
        "get-client-responses",
        "centralize-everything",
        "hit-client-deadlines",
      ],
    }
  },

  mounted() {
    this.checkScreenSize()
    this.$nextTick(() => {
      this.playVideo(this.currentStep)
    })
    window.addEventListener("resize", this.checkScreenSize)
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreenSize)
  },

  watch: {
    currentStep(newStep, oldStep) {
      this.stopVideo(oldStep)
      this.$nextTick(() => {
        this.playVideo(newStep)
      })
    },
    isLargeScreen() {
      this.$nextTick(() => {
        this.playVideo(this.currentStep)
      })
    },
  },

  methods: {
    nextStep() {
      if (this.currentStep === 5) {
        this.$emit("onboardingComplete")
        return
      }

      this.currentStep++
    },
    playVideo(step) {
      this.$nextTick(() => {
        const video = this.$refs["video" + step][0]
        if (video) {
          video.currentTime = 0
          video.playbackRate = step === 1 ? 1.2 : 1.5
          video.play().catch((error) => {
            console.warn("Video playback failed:", error)
          })
        }
      })
    },
    stopVideo(step) {
      const video = this.$refs["video" + step][0]
      if (video) {
        video.pause()
        video.currentTime = 0
      }
    },
    handleSkip() {
      this.$emit("onboardingComplete")
    },
    checkScreenSize() {
      this.isLargeScreen = window.innerWidth >= 1024
    },
  },
}
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.25s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

.video-container {
  height: 100%;
  position: relative;
  overflow: hidden;
}

.video-container video {
  top: 0;
  left: 0;
}
</style>
